/**
 * Runs the active "Show me" walk over whatever page is open, and shows the
 * tour's one-line toast (a save that failed). Mounted once in the app shell;
 * draws nothing until a lesson starts a walk or a save fails.
 *
 * The walk only points: Next moves to the next card, a step with its own
 * `route` opens that page first. The one thing it presses on the real page is
 * a form's opener from TOUR_OPENERS — never a control that saves or sends.
 */
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SpotlightOverlay } from './SpotlightOverlay';
import { useTourStore } from './tourState';
import { TOUR_OPENERS } from './tourWalks';

function TourToast() {
  const notice = useTourStore((s) => s.notice);
  const clearNotice = useTourStore((s) => s.clearNotice);
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(clearNotice, 4000);
    return () => window.clearTimeout(timer);
  }, [notice, clearNotice]);
  if (!notice) return null;
  return (
    <div role="status" className="card px-4 py-2 text-sm" style={{ position: 'fixed', zIndex: 90, left: '50%', bottom: 24, transform: 'translateX(-50%)', boxShadow: 'var(--sh-3)' }}>
      {notice}
    </div>
  );
}

export function TourSpotlightHost() {
  const walk = useTourStore((s) => s.walk);
  const index = useTourStore((s) => s.walkStep);
  const nextWalkStep = useTourStore((s) => s.nextWalkStep);
  const exitWalk = useTourStore((s) => s.exitWalk);
  const markDone = useTourStore((s) => s.markDone);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const step = walk?.steps[index];
  const route = step?.route;
  useEffect(() => {
    if (route && route !== pathname) navigate(route);
    // Only when the step changes: the walk opens the step's page once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walk, index]);

  // A step inside a form that is still closed: press the form's opener once (see TOUR_OPENERS).
  const opener = step?.open;
  const selector = step?.selector;
  useEffect(() => {
    if (!opener || !selector || !TOUR_OPENERS.includes(opener)) return;
    let tries = 0;
    const tryOpen = () => {
      tries += 1;
      if (document.querySelector(selector)) return true;
      const button = document.querySelector<HTMLElement>(opener);
      if (button) { button.click(); return true; }
      return tries >= 25;
    };
    if (tryOpen()) return;
    const timer = window.setInterval(() => { if (tryOpen()) window.clearInterval(timer); }, 200);
    return () => window.clearInterval(timer);
  }, [walk, index, opener, selector, pathname]);

  const last = !!walk && index >= walk.steps.length - 1;
  const backToLesson = useCallback(() => {
    if (!walk) return;
    exitWalk();
    navigate(`/tour/${walk.lessonId}`);
  }, [walk, exitWalk, navigate]);
  const onNext = useCallback(() => {
    if (!walk) return;
    if (!last) { nextWalkStep(); return; }
    void markDone(walk.lessonId);
    backToLesson();
  }, [walk, last, nextWalkStep, markDone, backToLesson]);

  return <>
    <TourToast />
    {walk && step && (
      <SpotlightOverlay
        target={step.selector}
        lessonTitle={walk.lessonTitle}
        step={index + 1}
        stepCount={walk.steps.length}
        title={step.title}
        body={step.body}
        onNext={onNext}
        onExit={backToLesson}
      />
    )}
  </>;
}
